import React from 'react';
import { Card, Button, Table, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TestHistoryEntry } from './types';
import { getRating, getRatingClass, calculatePerformanceScore } from './utils';

interface TestHistoryProps {
  testHistory: TestHistoryEntry[];
  copyTestHistoryToClipboard: () => void;
  clearTestHistory: () => void;
}

const TestHistory: React.FC<TestHistoryProps> = ({ 
  testHistory,
  copyTestHistoryToClipboard,
  clearTestHistory
}) => {
  const { t } = useTranslation('performance');

  return (
    <Card className="mt-2">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{t('history.title', 'Test History')}</h5>
        <div>
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={copyTestHistoryToClipboard}
            disabled={testHistory.length === 0}
            className="me-2"
          >
            {t('history.copyToClipboard', 'Copy to Clipboard (Markdown)')}
          </Button>
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={clearTestHistory}
            disabled={testHistory.length === 0}
          >
            {t('actions.clearHistory', 'Clear History')}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {testHistory.length === 0 ? (
          <div className="text-center text-muted">
            <p>{t('history.noHistory', 'No test history available. Run a test or test suite to collect performance data.')}</p>
          </div>
        ) : (
          <>
            <div className="alert alert-info mb-3">
              <strong>{t('history.resultsHeader', 'Test Results')}:</strong> {testHistory.length} tests completed.
              {testHistory.length > 1 && (
                <span> Compare different configurations to find the optimal settings for your needs.</span>
              )}
            </div>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t('history.timeHeader', 'Time')}</th>
                    <th>{t('history.configHeader', 'Configuration')}</th>
                    <th>{t('metrics.responseTime.title', 'Response Time')}</th>
                    <th>{t('metrics.successRate.title', 'Success Rate')}</th>
                    <th>{t('metrics.throughput.title', 'Throughput')}</th>
                    <th>Assessment</th>
                  </tr>
                </thead>
                <tbody>
                  {testHistory.map((entry, index) => {
                    // Calculate a simple performance score for sorting and comparison
                    // Lower is better - factors in response time and error rate
                    const perfScore = calculatePerformanceScore(
                      entry.metrics.averageResponseTime,
                      entry.metrics.successRate
                    );
                    
                    // Get the previous entry for comparison if available
                    const prevEntry = index < testHistory.length - 1 ? testHistory[index + 1] : null;
                    
                    // Calculate deltas for comparison with previous test
                    const responseTimeDelta = prevEntry 
                      ? entry.metrics.averageResponseTime - prevEntry.metrics.averageResponseTime
                      : 0;
                      
                    const successRateDelta = prevEntry
                      ? entry.metrics.successRate - prevEntry.metrics.successRate
                      : 0;
                      
                    return (
                      <tr key={index} className={
                        perfScore < 10 ? "table-success" : 
                        perfScore < 20 ? "" : 
                        perfScore < 30 ? "table-warning" : "table-danger"
                      }>
                        <td>{entry.date.toLocaleTimeString()}</td>
                        <td>
                          <div><strong>{t('history.batchSize', 'Batch Size')}:</strong> {entry.config.batchSize}</div>
                          <div><strong>{t('history.batchInterval', 'Interval')}:</strong> {entry.config.batchInterval}ms</div>
                          <div><strong>{t('history.clicks', 'Clicks/sec')}:</strong> {entry.config.clicksPerSecond}/sec</div>
                          <div><strong>{t('history.duration', 'Duration')}:</strong> {entry.config.testDuration}s</div>
                        </td>
                        <td>
                          <div>{entry.metrics.averageResponseTime.toFixed(1)}ms</div>
                          <div className="text-muted">Max: {entry.metrics.maxResponseTime.toFixed(1)}ms</div>
                          {prevEntry && 
                            <div className={responseTimeDelta < 0 ? 'text-success' : 'text-danger'}>
                              {responseTimeDelta < 0 ? '▼' : '▲'} {Math.abs(responseTimeDelta).toFixed(1)}ms from previous
                            </div>
                          }
                        </td>
                        <td>
                          <div>{entry.metrics.successRate.toFixed(2)}%</div>
                          <div className="text-muted">
                            {entry.metrics.failedClicks} failed / {entry.metrics.totalClicks} total
                          </div>
                          {prevEntry && 
                            <div className={successRateDelta < 0 ? 'text-danger' : 'text-success'}>
                              {successRateDelta < 0 ? '▼' : '▲'} {Math.abs(successRateDelta).toFixed(2)}% from previous
                            </div>
                          }
                        </td>
                        <td>
                          <div>{entry.metrics.clicksPerSecond.toFixed(1)}/sec</div>
                          <span className={`badge bg-${getRating('throughput', entry.metrics.clicksPerSecond) === 'Stress Level' ? 'danger' : 
                                                     getRating('throughput', entry.metrics.clicksPerSecond) === 'High Load' ? 'warning' : 
                                                     getRating('throughput', entry.metrics.clicksPerSecond) === 'Typical Load' ? 'success' : 'info'}`}>
                            {getRating('throughput', entry.metrics.clicksPerSecond)}
                          </span>
                        </td>
                        <td>
                          <div>
                            <span className={getRatingClass(getRating('responseTime', entry.metrics.averageResponseTime))}>
                              Response Time: {getRating('responseTime', entry.metrics.averageResponseTime)}
                            </span>
                          </div>
                          <div>
                            <span className={getRatingClass(getRating('successRate', entry.metrics.successRate))}>
                              Success Rate: {getRating('successRate', entry.metrics.successRate)}
                            </span>
                          </div>
                          <div className="mt-1">
                            {perfScore < 10 ? 
                              <Badge bg="success">Optimal Configuration</Badge> : 
                            perfScore < 20 ? 
                              <Badge bg="primary">Good Configuration</Badge> :
                            perfScore < 30 ? 
                              <Badge bg="warning">Needs Improvement</Badge> :
                              <Badge bg="danger">Poor Performance</Badge>
                            }
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default TestHistory; 